import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Navbar, LeftPanel, RightPanel, NotesPanel, Employee, CustomerInfo } from './components';
import OrderPage from './pages/OrderPage';
import LoginPage from './pages/LoginPage';
import { Item } from './types/Item';
import './styles/index.css';

const employees: Employee[] = [
  { 
    id: '1', 
    name: 'Lizzy Anker', 
    initials: 'LA', 
    color: '#4A90E2', 
    profilePic: '/LizzyAnkerProfilePic.jpg',
    bio: 'Lizzy is .'
  },
  { 
    id: '2', 
    name: 'Lars Geilings', 
    initials: 'LA', 
    color: '#E24A4A', 
    profilePic: '/LarsGeilingsProfilePic.jpeg',
    bio: 'Lars is'
  },
  { 
    id: '3', 
    name: 'Rob van Sambeeck', 
    initials: 'RvS', 
    color: '#4AE24A', 
    profilePic: '/RobVanSambeeckProfilePic.png',
    bio: 'Rob is'
  }
];

// Helper functions for employee-specific localStorage keys
const getStorageKey = (key: string, employeeId: string) => `${key}_${employeeId}`;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loggedInEmployee, setLoggedInEmployee] = useState<{ id: string; name: string; username: string } | null>(() => {
    const saved = localStorage.getItem('loggedInEmployee');
    return saved ? JSON.parse(saved) : null;
  });

  // Check if user is logged in, if not redirect will happen in routes
  useEffect(() => {
    const saved = localStorage.getItem('loggedInEmployee');
    if (saved) {
      const loggedIn = JSON.parse(saved);
      setLoggedInEmployee(loggedIn);
    }
  }, []);

  const [availableItems, setAvailableItems] = useState<Item[]>(() => {
    // Default new items list - ALWAYS use this as the source of truth
    const defaultItems = [
      { id: '1', name: 'Bedrijfsprofiel', price: 250 },
      { id: '2', name: 'Blog', price: 200 },
      { id: '3', name: 'Social post', price: 100 },
      { id: '4', name: 'Pinterest Pin', price: 100 },
      { id: '5', name: 'LinkedIn Post', price: 100 },
      { id: '6', name: 'Videoproductie', price: 150 },
      { id: '7', name: 'Video content maken', price: 0 },
      { id: '8', name: 'Story', price: 100 },
      { id: '9', name: 'Nieuwsbrief op maat', price: 500 },
      { id: '10', name: 'Meta campagne', price: 500 },
      { id: '11', name: 'LinkedIn campagne', price: 500 },
      { id: '12', name: 'Pinterest campagne', price: 500 },
      { id: '13', name: 'Vermelding in algemene nieuwsbrief', price: 250 },
      { id: '14', name: 'Reels op Tiktok of Instagram', price: 150 },
      { id: '15', name: 'Stijlgids opname', price: 500 },
      { id: '16', name: 'Promotiebudget', price: 0 }
    ];
    
    // Force update: Remove old localStorage and set new items
    localStorage.removeItem('availableItems');
    localStorage.setItem('availableItems', JSON.stringify(defaultItems));
    return defaultItems;
  });
  
  const [canvasItems, setCanvasItems] = useState<Item[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    companyName: '',
    email: '',
    phone: '',
    contactPerson: ''
  });
  const [discount, setDiscount] = useState<number>(0);
  const [showDiscount, setShowDiscount] = useState<boolean>(false);
  const [, setIsNotesExpanded] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee>(() => {
    // Load from localStorage or use logged in employee
    const saved = localStorage.getItem('selectedEmployee');
    const loggedIn = loggedInEmployee;
    
    if (loggedIn) {
      // Use logged in employee
      const fullEmployee = employees.find(emp => emp.id === loggedIn.id);
      if (fullEmployee) {
        localStorage.setItem('selectedEmployee', JSON.stringify(fullEmployee));
        return fullEmployee;
      }
    }
    
    if (saved) {
      const parsedEmployee = JSON.parse(saved);
      // Always get the full employee data to ensure we have all contact info
      const fullEmployee = employees.find(emp => emp.id === parsedEmployee.id);
      if (fullEmployee) {
        // Update localStorage with complete employee data
        localStorage.setItem('selectedEmployee', JSON.stringify(fullEmployee));
        return fullEmployee;
      }
      return parsedEmployee;
    }
    return employees[0];
  });

  // Update selected employee when logged in employee changes
  useEffect(() => {
    if (loggedInEmployee) {
      const fullEmployee = employees.find(emp => emp.id === loggedInEmployee.id);
      if (fullEmployee) {
        setSelectedEmployee(fullEmployee);
        localStorage.setItem('selectedEmployee', JSON.stringify(fullEmployee));
      }
    }
  }, [loggedInEmployee]);

  // Force update availableItems on mount to ensure new list is always used
  useEffect(() => {
    const defaultItems = [
      { id: '1', name: 'Bedrijfsprofiel', price: 250 },
      { id: '2', name: 'Blog', price: 200 },
      { id: '3', name: 'Social post', price: 100 },
      { id: '4', name: 'Pinterest Pin', price: 100 },
      { id: '5', name: 'LinkedIn Post', price: 100 },
      { id: '6', name: 'Videoproductie', price: 150 },
      { id: '7', name: 'Video content maken', price: 0 },
      { id: '8', name: 'Story', price: 100 },
      { id: '9', name: 'Nieuwsbrief op maat', price: 500 },
      { id: '10', name: 'Meta campagne', price: 500 },
      { id: '11', name: 'LinkedIn campagne', price: 500 },
      { id: '12', name: 'Pinterest campagne', price: 500 },
      { id: '13', name: 'Vermelding in algemene nieuwsbrief', price: 250 },
      { id: '14', name: 'Reels op Tiktok of Instagram', price: 150 },
      { id: '15', name: 'Stijlgids opname', price: 500 },
      { id: '16', name: 'Promotiebudget', price: 0 }
    ];
    
    // Force update: Remove old localStorage and set new items
    localStorage.removeItem('availableItems');
    localStorage.setItem('availableItems', JSON.stringify(defaultItems));
    setAvailableItems(defaultItems);
  }, []);

  // Load employee-specific data when selectedEmployee changes
  useEffect(() => {
    if (selectedEmployee?.id) {
      const employeeId = selectedEmployee.id;
      
      // Load employee-specific data - each employee has their own separate data
      const savedCanvasItems = localStorage.getItem(getStorageKey('canvasItems', employeeId));
      setCanvasItems(savedCanvasItems ? JSON.parse(savedCanvasItems) : []);
      
      const savedNotes = localStorage.getItem(getStorageKey('notes', employeeId));
      setNotes(savedNotes || '');
      
      const savedCustomerInfo = localStorage.getItem(getStorageKey('customerInfo', employeeId));
      setCustomerInfo(savedCustomerInfo ? JSON.parse(savedCustomerInfo) : {
        companyName: '',
        email: '',
        phone: '',
        contactPerson: ''
      });
      
      const savedDiscount = localStorage.getItem(getStorageKey('discount', employeeId));
      setDiscount(savedDiscount ? parseFloat(savedDiscount) : 0);
      
      const savedShowDiscount = localStorage.getItem(getStorageKey('showDiscount', employeeId));
      setShowDiscount(savedShowDiscount ? JSON.parse(savedShowDiscount) : false);
    }
  }, [selectedEmployee]);

  const addItem = (name: string, price: number) => {
    const newItem: Item = {
      id: Date.now().toString(),
      name,
      price,
    };
    setAvailableItems(prev => {
      const newAvailableItems = [newItem, ...prev];
      localStorage.setItem('availableItems', JSON.stringify(newAvailableItems));
      return newAvailableItems;
    });
  };

  const moveItemToCanvas = (item: Item) => {
    if (!selectedEmployee?.id) return;
    
    setCanvasItems(prev => {
      const newCanvasItems = [...prev, { ...item, id: Date.now().toString() }];
      localStorage.setItem(getStorageKey('canvasItems', selectedEmployee.id), JSON.stringify(newCanvasItems));
      return newCanvasItems;
    });
  };

  const removeItemFromCanvas = (itemId: string) => {
    if (!selectedEmployee?.id) return;
    
    setCanvasItems(prev => {
      const newCanvasItems = prev.filter(item => item.id !== itemId);
      localStorage.setItem(getStorageKey('canvasItems', selectedEmployee.id), JSON.stringify(newCanvasItems));
      return newCanvasItems;
    });
  };

  const handleNotesChange = (newNotes: string) => {
    if (!selectedEmployee?.id) return;
    
    setNotes(newNotes);
    localStorage.setItem(getStorageKey('notes', selectedEmployee.id), newNotes);
  };

  const handleCustomerInfoChange = (newCustomerInfo: CustomerInfo) => {
    if (!selectedEmployee?.id) return;
    
    setCustomerInfo(newCustomerInfo);
    localStorage.setItem(getStorageKey('customerInfo', selectedEmployee.id), JSON.stringify(newCustomerInfo));
  };

  const handleDiscountChange = (newDiscount: number) => {
    if (!selectedEmployee?.id) return;
    
    setDiscount(newDiscount);
    localStorage.setItem(getStorageKey('discount', selectedEmployee.id), newDiscount.toString());
  };

  const handleToggleDiscount = () => {
    if (!selectedEmployee?.id) return;
    
    setShowDiscount(prev => {
      const newValue = !prev;
      localStorage.setItem(getStorageKey('showDiscount', selectedEmployee.id), JSON.stringify(newValue));
      
      // Als korting wordt uitgezet, reset ook de korting waarde naar 0
      if (!newValue) {
        setDiscount(0);
        localStorage.removeItem(getStorageKey('discount', selectedEmployee.id));
      }
      
      return newValue;
    });
  };

  const handleEmployeeChange = (employee: Employee) => {
    setSelectedEmployee(employee);
    localStorage.setItem('selectedEmployee', JSON.stringify(employee));
  };

  useEffect(() => {
    const handlePageShow = () => {
      if (!localStorage.getItem('loggedInEmployee')) {
        navigate('/login', { replace: true });
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [navigate]);

  const resetStateForLogout = () => {
    setLoggedInEmployee(null);
    setSelectedEmployee(employees[0]);
    setCanvasItems([]);
    setNotes('');
    setCustomerInfo({
      companyName: '',
      email: '',
      phone: '',
      contactPerson: ''
    });
    setDiscount(0);
    setShowDiscount(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('loggedInEmployee');
    localStorage.removeItem('selectedEmployee');
    localStorage.removeItem('authToken');
    resetStateForLogout();
    navigate('/login', { replace: true });
  };

  const handleResetPackage = () => {
    if (!selectedEmployee?.id) return;
    
    // Show confirmation first
    if (window.confirm('Weet je zeker dat je een nieuw pakket wilt starten? Alle huidige items en notities worden gewist.')) {
      // Reset to default available items
      const defaultItems = [
        { id: '1', name: 'Bedrijfsprofiel', price: 250 },
        { id: '2', name: 'Blog', price: 200 },
        { id: '3', name: 'Social post', price: 100 },
        { id: '4', name: 'Pinterest Pin', price: 100 },
        { id: '5', name: 'LinkedIn Post', price: 100 },
        { id: '6', name: 'Videoproductie', price: 150 },
        { id: '7', name: 'Video content maken', price: 0 },
        { id: '8', name: 'Story', price: 100 },
        { id: '9', name: 'Nieuwsbrief op maat', price: 500 },
        { id: '10', name: 'Meta campagne', price: 500 },
        { id: '11', name: 'LinkedIn campagne', price: 500 },
        { id: '12', name: 'Pinterest campagne', price: 500 },
        { id: '13', name: 'Vermelding in algemene nieuwsbrief', price: 250 },
        { id: '14', name: 'Reels op Tiktok of Instagram', price: 150 },
        { id: '15', name: 'Stijlgids opname', price: 500 },
        { id: '16', name: 'Promotiebudget', price: 0 }
      ];
      setAvailableItems(defaultItems);
      localStorage.setItem('availableItems', JSON.stringify(defaultItems));
      
      // Clear canvas items for this employee
      setCanvasItems([]);
      localStorage.removeItem(getStorageKey('canvasItems', selectedEmployee.id));
      
      // Reset discount for this employee
      setDiscount(0);
      localStorage.removeItem(getStorageKey('discount', selectedEmployee.id));
      
      // Clear notes for this employee
      setNotes('');
      localStorage.removeItem(getStorageKey('notes', selectedEmployee.id));
      
      // Clear customer info for this employee
      setCustomerInfo({
        companyName: '',
        email: '',
        phone: '',
        contactPerson: ''
      });
      localStorage.removeItem(getStorageKey('customerInfo', selectedEmployee.id));
      
      // Reset showDiscount for this employee
      setShowDiscount(false);
      localStorage.removeItem(getStorageKey('showDiscount', selectedEmployee.id));
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="app">
        <Navbar 
          selectedEmployee={selectedEmployee}
          onEmployeeChange={handleEmployeeChange}
          employees={employees}
          onResetPackage={handleResetPackage}
          hideEmployeeSelector={true}
          onLogout={handleLogout}
        />
          <div className="main-content">
            <div className="panels-container">
              <div className="main-panels">
                <LeftPanel
                  items={availableItems}
                  onAddItem={addItem}
                  onMoveItem={moveItemToCanvas}
                />
                <RightPanel
                  items={canvasItems}
                  onRemoveItem={removeItemFromCanvas}
                  notes={notes}
                  selectedEmployee={selectedEmployee}
                  discount={discount}
                  onDiscountChange={handleDiscountChange}
                  showDiscount={showDiscount}
                />
              </div>
            </div>
            <NotesPanel
              notes={notes}
              onNotesChange={handleNotesChange}
              customerInfo={customerInfo}
              onCustomerInfoChange={handleCustomerInfoChange}
              onToggleExpanded={setIsNotesExpanded}
              onToggleDiscount={handleToggleDiscount}
              showDiscount={showDiscount}
            />
          </div>
      </div>
    </DndProvider>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const loggedIn = localStorage.getItem('loggedInEmployee');
  
  if (!loggedIn) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  // Redirect root path based on login status
  const RootRedirect = () => {
    const loggedIn = localStorage.getItem('loggedInEmployee');
    return <Navigate to={loggedIn ? "/dashboard" : "/login"} replace />;
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pakketoverzicht" 
          element={
            <ProtectedRoute>
              <OrderPage />
            </ProtectedRoute>
          } 
        />
        {/* Keep /order for backwards compatibility */}
        <Route 
          path="/order" 
          element={
            <ProtectedRoute>
              <OrderPage />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </Router>
  );
};

export default App;
